import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpContextToken
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import Keycloak from 'keycloak-js';
import { Observable, from } from "rxjs";
import { switchMap } from 'rxjs/operators';
import { USER_MGMT_CNST } from "../../constants/proj.cnst";
import { AuthenticationService } from "../../modules/authentication/services/authentication.service";
import { AppStateService } from "../../services/app-state.service";
import { CsrfTokenService } from "../../services/csrf-token.service";
import { UtilsService } from "../../services/utils.service";
import { Router } from "@angular/router";
import { isNullOrUndefined } from "../../shared/common-utils";
import { APIMapping } from "../../shared/vl-http/api-mapping.cnst";
import { KeycloakService } from "../../services/keycloak.service";

// Create a context token to identify when to skip headers
export const SKIP_INTERCEPTOR = new HttpContextToken(() => false);
@Injectable()
export class AuthInterceptorService implements HttpInterceptor {

  constructor(
    private authSrv: AuthenticationService,
    private csrfTokenSrv: CsrfTokenService,
    private utilSrv: UtilsService,
    private appStateSrv: AppStateService,
    private readonly route: Router,
    private keyCloakSrv : KeycloakService
  ) { 
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.fetchDetails(request)).pipe(
      switchMap((result: any) => {
        if (isNullOrUndefined(result)) {
          return next.handle(request);
        }
        if (request.context.get(SKIP_INTERCEPTOR)) {
          return next.handle(request);
        }
        request = this.trialSpace(request);
        const csrfToken = this.csrfTokenSrv.getCsrfToken();
        const isCsrfGenerationUrl = request.url.includes(USER_MGMT_CNST.API_MAPPING.GET_CSRF_TOKEN);
        const userMetaInfo = this.appStateSrv.getUserInfoFromLocalStorage();
        const customerUuid = this.appStateSrv.getOrganizationInfo()?.customerId ?? userMetaInfo.customerId;

        if (!isNullOrUndefined(result.currentUser) && result.currentUser["accessToken"]) {
          let additionalHeaders: HttpHeaders = this.buildAdditionalHeaders(isCsrfGenerationUrl, result.currentUser, csrfToken, request, userMetaInfo, customerUuid);
          let modifiedRequest = request.clone({ headers: additionalHeaders });

          if (USER_MGMT_CNST.DOMAINS_NEEDING_USER_ID.some(item => request.url.includes(item))) {
            modifiedRequest = modifiedRequest.clone({
              params: (request.params ? request.params : new HttpParams()).set(
                "email",
                `${result.currentUser["userId"]}`
              )
            });
          }

          return next.handle(modifiedRequest);
        } else {
          return next.handle(request);
        }
      })
    );
  }

  //Modifies URL of HttpRequest to replace trialspace
  trialSpace(request: HttpRequest<any>){
    let modifiedUrl = request.url;
    let trimmedUrl = modifiedUrl.trimEnd();
    let trailingWhitespaceLength = modifiedUrl.length - trimmedUrl.length;
    modifiedUrl = trimmedUrl + '%20'.repeat(trailingWhitespaceLength);
    return request.clone({ url: modifiedUrl });
  }

  buildAdditionalHeaders(isCsrfGenerationUrl: boolean, currentUser: any, csrfToken: string | null, request: any, userMetaInfo: any, customerUuid: string | null): HttpHeaders {
    let additionalHeaders: HttpHeaders = request.headers;
    csrfToken = isNullOrUndefined(csrfToken) ? localStorage.getItem('csrfToken') : csrfToken;


    if (isCsrfGenerationUrl) {
      additionalHeaders = new HttpHeaders({
        'Authorization': `Bearer ${currentUser["accessToken"]}`
      });
    } else if (!isNullOrUndefined(csrfToken)) {
      const applicationId = this.appStateSrv.getApplicationInfo()?.applicationId ?? this.getApplicationIdOfFirstApplication();
      const headersObj: { [key: string]: string } = {
        'Authorization': `Bearer ${currentUser["accessToken"]}`,
        'Csrf-Token': csrfToken as string,
        'ngrok-skip-browser-warning': 'true'
      };

      if (Object.entries(userMetaInfo).length !== 0) {
        headersObj['user-uuid'] = userMetaInfo.userId;
        headersObj['role_name'] = this.appStateSrv.getUserRoleName() ?? '';
        if (customerUuid) {
          headersObj['customer-uuid'] = customerUuid;
        }
      }

      additionalHeaders = this.setHeaders(additionalHeaders, headersObj);
    }

    return additionalHeaders;
  }

  private setHeaders(headers: HttpHeaders, headersObj: { [key: string]: string }): HttpHeaders {
    for (const [key, value] of Object.entries(headersObj)) {
      headers = headers.set(key, value);
    }
    return headers;
  }


  private loadUserInfo() {
    const userInfo =localStorage.getItem('userInfo')
    if(userInfo) {
      const userDetails = JSON.parse(userInfo);
      return {
        accessToken : localStorage.getItem('token'),
        userId : userDetails?.userId || null
      }
    }
    return null;
  }

  private fetchDetails(request: HttpRequest<any>): Promise<any> {
    // const keycloakInstance = this.appStateSrv.getKeyCloakInstance();
    const keycloakInstance = this.keyCloakSrv.getKeycloakInstance()

    const result: {
      currentUser: any,
      token: string | null
    } = {
      currentUser: null,
      token: null
    };

    return new Promise((resolve) => {
      (async () => {
        if (request.url.includes('.json') || request.url.includes('social-login') || request.url.includes('csrf') ) {
          resolve(null);
          return;
        }

        const isTokenRefreshed = await this.refreshTokenIfNeeded(keycloakInstance);

        const finalToken = this.determineFinalToken(isTokenRefreshed, keycloakInstance);

        this.storeTokenIfUpdated(keycloakInstance, finalToken);

        result.currentUser = this.loadUserInfo();
        result.token = finalToken;

        resolve(result);
      })();
    });
  }

  private async refreshTokenIfNeeded(keycloakInstance: Keycloak): Promise<boolean | null> {
    if (isNullOrUndefined(keycloakInstance)) {
      return null;
    }
    return await this.checkForTokenExpiryAndRefresh(keycloakInstance);
  }

  private determineFinalToken(isTokenRefreshed: boolean | null, keycloakInstance: Keycloak): string | null {
    const existingToken = localStorage.getItem('token');
    if (isTokenRefreshed) {
      return keycloakInstance.token ?? null;
    }
    if (existingToken === 'null') {
      return JSON.parse(existingToken) ?? null;
    }
    return existingToken;
  }

  private storeTokenIfUpdated(keycloakInstance: Keycloak, finalToken: string | null) {
    if (!isNullOrUndefined(keycloakInstance) && !isNullOrUndefined(keycloakInstance.token)) {
      const existingToken = localStorage.getItem('token');
      if (existingToken !== keycloakInstance.token) {
        localStorage.setItem('token', keycloakInstance.token ?? '');
      }
    }
  }

  private checkForTokenExpiryAndRefresh(keycloak: Keycloak): Promise<boolean | null> {
    return new Promise((resolve) => {
      (async () => {
        if (keycloak) {
          keycloak.onAuthRefreshError = () => {
            this.utilSrv.showToastMessage("Refresh Token Expired. Logging out", "error");
            this.utilSrv.logout();
          };

          try {
            const isTokenRefreshed = await keycloak.updateToken(20);
            resolve(isTokenRefreshed);
          } catch (error) {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      })();
    });
  }

  private getApplicationIdOfFirstApplication() {
    const permissions = this.appStateSrv.getUserPermissions();
    const applicationNames = Object.keys(permissions ?? {});
    if (applicationNames.length > 0) {
      return permissions[applicationNames[0]]?.['applicationId'];
    } else {
      return null;
    }
  }
}
